import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Trophy, Home, User as UserIcon, Shield, LogOut, Users, BarChart3, GitBranch } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { InstallBanner } from "@/components/InstallBanner";
import { PullToRefresh } from "@/components/PullToRefresh";
import logoEnglputzeder from "@/assets/logo-englputzeder.png";
import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { loadGroupRankings } from "@/lib/ranking";


// `wide` lets a route break out of the default mobile-first max-w-2xl. The
// admin page uses it because settings + game lists + tipper roster need
// horizontal room they don't get on a narrow column.
export function AppShell({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  const { profile, isAdmin, signOut, user } = useAuth();
  const nav = useNavigate();
  const [bestGroupRank, setBestGroupRank] = useState<number | null>(null);

  useEffect(() => {
    if (!user) { setBestGroupRank(null); return; }
    let alive = true;
    (async () => {
      const [groups, gRanks] = await Promise.all([
        api.groups.mine() as Promise<any[]>,
        loadGroupRankings(),
      ]);
      if (!alive) return;
      const ids = new Set((groups ?? []).map((g: any) => g.id));
      const mine = gRanks.filter(g => ids.has(g.group_id));
      if (mine.length === 0) { setBestGroupRank(null); return; }
      setBestGroupRank(Math.min(...mine.map(g => g.rank)));
    })();
    return () => { alive = false; };
  }, [user]);

  const navCls = ({ isActive }: { isActive: boolean }) =>
    `flex flex-col items-center justify-center gap-0.5 min-w-[64px] flex-1 py-2 text-[10px] font-medium tap border-t-2 ${
      isActive ? "text-primary-foreground border-primary-foreground" : "text-primary-foreground/70 border-transparent"
    }`;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Renders nothing in a normal browser tab — only intercepts
          gestures when running as an installed standalone PWA, where
          iOS otherwise suppresses native pull-to-refresh. */}
      <PullToRefresh />
      {/* Header. The `pt-[env(safe-area-inset-top)]` keeps the logo + avatar
          out from under the iOS Dynamic Island / notch when the app is
          installed as a PWA on the home screen. In regular browser tabs
          the inset is 0, so this is a no-op there. Same trick on the
          bottom nav for the home-indicator gesture bar. */}
      <header className="sticky top-0 z-40 bg-primary text-primary-foreground shadow-pop pt-[env(safe-area-inset-top)]">
        <div className={`container ${wide ? "max-w-6xl" : "max-w-2xl"} flex items-center justify-between py-3`}>
          <Link to="/" className="flex items-center gap-2 tap">
            <span className="bg-white rounded-md px-1.5 py-1 inline-flex items-center">
              <img src={logoEnglputzeder} alt="Englputzeder" className="h-4 w-auto" />
            </span>
            <span className="font-display font-bold text-base text-primary-foreground/90">Tippspiel</span>
          </Link>
          {profile && (
            <div className="flex items-center gap-2">
              
              <button
                onClick={() => nav("/profil")}
                className="flex items-center gap-2 tap rounded-pill bg-primary-foreground/10 px-1.5 py-1"
                aria-label="Profil"
              >
                <UserAvatar url={profile.avatarUrl} emoji={profile.emoji} size={28} />
                <span className="hidden xs:inline text-[11px] font-semibold pr-1.5">{profile.username}</span>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main */}
      <main className={`flex-1 container ${wide ? "max-w-6xl" : "max-w-2xl"} py-4 pb-24 animate-fade-in`}>{children}</main>

      {/* Legal disclaimer */}
      <div className={`container ${wide ? "max-w-6xl" : "max-w-2xl"} px-4 pb-20 -mt-4`}>
        <p className="text-[10px] text-muted-foreground text-center leading-snug">
          Diese Aktion steht in keinerlei Verbindung mit der FIFA oder offiziellen Sponsoren der FIFA Fußball-Weltmeisterschaft 2026.
        </p>
      </div>

      {/* Bottom nav — safe-area padding mirrors the header so the iOS
          home-indicator bar doesn't sit on top of the nav buttons when
          installed as a PWA. */}
      {profile && (
        <nav className="fixed bottom-0 inset-x-0 z-40 bg-primary text-primary-foreground border-t border-primary-foreground/10 pb-[env(safe-area-inset-bottom)]">
          <div className={`container ${wide ? "max-w-6xl" : "max-w-2xl"} flex items-stretch px-0 overflow-x-auto`}>
            <NavLink to="/" end className={navCls}>
              <Home className="h-5 w-5" /> Start
            </NavLink>
            <NavLink to="/ko-phase" className={navCls} data-tour="nav-tabellen">
              <GitBranch className="h-5 w-5" /> Tabellen
            </NavLink>
            <NavLink to="/ranking" className={navCls}>
              <Trophy className="h-5 w-5" /> Ranking
            </NavLink>
            <NavLink to="/gruppen" className={navCls} data-tour="nav-gruppen">
              <div className="relative">
                <Users className="h-5 w-5" />
                {bestGroupRank !== null && (
                  <span
                    className={`absolute -top-1.5 -right-2 text-[9px] font-extrabold rounded-pill px-1 leading-tight tabular-nums ${
                      bestGroupRank === 1
                        ? "bg-gold text-foreground"
                        : bestGroupRank === 2
                        ? "bg-silver text-foreground"
                        : bestGroupRank === 3
                        ? "bg-bronze text-foreground"
                        : "bg-accent text-accent-foreground"
                    }`}
                  >
                    #{bestGroupRank}
                  </span>
                )}
              </div>
              Gruppen
            </NavLink>
            <NavLink to="/profil" className={navCls}>
              <UserIcon className="h-5 w-5" /> Profil
            </NavLink>
            {isAdmin && (
              <NavLink to="/admin" className={navCls}>
                <Shield className="h-5 w-5" /> Admin
              </NavLink>
            )}
            <button onClick={() => signOut()} className="flex flex-col items-center justify-center gap-0.5 min-w-[64px] flex-1 py-2 text-[10px] font-medium text-primary-foreground/70 tap">
              <LogOut className="h-5 w-5" /> Logout
            </button>
          </div>
        </nav>
      )}

      {/* PWA install nudger — self-hides if already installed / dismissed.
          Sits above the bottom nav, dismissible, quiets itself for 2 weeks
          after a manual close. iOS Safari gets an instruction sheet
          instead of a programmatic prompt. */}
      {profile && <InstallBanner />}
    </div>
  );
}
